import { useCallback, useMemo, useState } from 'react';
import { Archive, Ban, Check, FilePlus2, Hourglass, Pause, Play } from 'lucide-react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PAGES } from '../../constants/pages';

import { ActionBar } from 'components/ActionBar';
import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';

import { BundleState, ColumnType, useUpdateBundleStateMutation } from 'data/__generated__';

import { BoxToPrint, PrintBoxTags } from './components/PrintBoxTags';
import { BundlesList } from './components/BundlesList';
import { FlowSelectionButtons } from './components/FlowSelectionButtons';
import { BundleRow } from './constants/bundlesColumns';
import { BUNDLE_STATES_LABELS, canTransitionBundleState } from './constants/states';

// Ordered by the natural preparation lifecycle so the action bar reads top-to-bottom.
const STATE_ACTIONS: { state: BundleState; icon: React.ReactNode }[] = [
    { state: BundleState.Created, icon: <FilePlus2 /> },
    { state: BundleState.Started, icon: <Play /> },
    { state: BundleState.InProgress, icon: <Hourglass /> },
    { state: BundleState.Paused, icon: <Pause /> },
    { state: BundleState.Completed, icon: <Check /> },
    { state: BundleState.Aborted, icon: <Ban /> },
    { state: BundleState.Archived, icon: <Archive /> },
];

export const Bundles = () => {
    const [bundles, setBundles] = useState<BundleRow[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [updateBundleState, { loading: updateStateLoading }] = useUpdateBundleStateMutation();

    const selectedBundles = useMemo(() => {
        const selectedIdSet = new Set(selectedIds);
        return bundles.filter((bundle) => selectedIdSet.has(bundle._id));
    }, [bundles, selectedIds]);

    const handleUpdateState = useCallback(
        async (newState: BundleState) => {
            if (!selectedBundles.length) return;

            const results = await Promise.all(
                selectedBundles.map(({ _id }) => updateBundleState({ variables: { bundleId: _id, state: newState } })),
            );

            const failures = results.filter((result) => !result.data || result.data.updateBundleState.error);
            const successCount = selectedBundles.length - failures.length;
            const stateLabel = BUNDLE_STATES_LABELS[newState] ?? newState;

            if (successCount) {
                toast.success(`${successCount} bundle(s) passé(s) à "${stateLabel}"`);
            }
            if (failures.length) {
                toast.error(`${failures.length} bundle(s) sur ${selectedBundles.length} n'ont pas pu être mis à jour`);
            }

            setSelectedIds([]);
        },
        [selectedBundles, updateBundleState],
    );

    const actionSections = useMemo(
        () => [
            {
                title: 'Changer le statut',
                actions: STATE_ACTIONS.map(({ state, icon }) => ({
                    icon,
                    label: BUNDLE_STATES_LABELS[state] ?? state,
                    onClick: () => handleUpdateState(state),
                    // Grey out a target unless every selected bundle is allowed to reach it,
                    // applying the same transition rules the back enforces in updateBundleState.
                    disabled:
                        !selectedBundles.length ||
                        selectedBundles.some((bundle) => !canTransitionBundleState(bundle.state, state)),
                    loading: updateStateLoading,
                })),
            },
        ],
        [selectedBundles, updateStateLoading, handleUpdateState],
    );

    const boxesToPrint: BoxToPrint[] = selectedBundles.flatMap((bundle) => {
        const { boxes, number: bundleNumber, containerType } = bundle;
        return boxes.map(({ label, hasOnsiteSetup, orderId, roundColor, roundName, code, deliveryCode, column }) => ({
            label,
            hasOnsiteSetup,
            isFreefood: !!orderId,
            bundleNumber,
            roundColor,
            roundName,
            code: code || '',
            deliveryCode: deliveryCode || '',
            containerType,
            columnName: column?.type === ColumnType.SecuredMicrostore ? column.name : null,
        }));
    });

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.bundles} />
                </HeaderTitle>
                <PrintBoxTags boxesToPrint={boxesToPrint} />
            </Header>
            <FlowSelectionButtons bundles={bundles} selectedIds={selectedIds} onSelectionChange={setSelectedIds} />
            <ContentContainer>
                <Content>
                    <BundlesList
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        onBundlesChange={setBundles}
                    />
                </Content>
                <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedBundles.length} />
            </ContentContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ContentContainer = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
    padding: 15px;
`;
